/******************************
* Based in written-number npm *
******************************/

import { WrittenNumberOptions } from './WrittenNumberOptions'

export class WrittenNumber {
  private _options: WrittenNumberOptions
  // eslint-disable-next-line
  private _language: any
  // eslint-disable-next-line
  private _firstSignificant: any
  private _i18n = {
    es: require('./i18n/es.json'),
    gl: require('./i18n/gl.json')
  }

  private _scale = [100, 1000]

  constructor (options: WrittenNumberOptions) {
    for (let i = 1; i <= 15; i++) {
      this._scale.push(Math.pow(10, i * 6))
    }
    this._options = options
  }

  writtenNumber (n: number): string {
    if (n < 0 || n % 1 !== 0) {
      return ''
    }

    let res: string[] = []
    // Select language
    this.selectLanguage()

    // Check exceptions
    if (this._language.unitExceptions[n]) return this._language.unitExceptions[n]
    if (this._language.base[n]) return this._language.base[n]
    if (n < 100) { return this.handleSmallerThan100(n) }
    const m = n % 100

    // Checks divided items
    if (m) {
      if (this._options.noAnd() &&
        !(this._language.andException && this._language.andException[10])) {
        res.push(this.writtenNumber(m))
      } else {
        res.push(this._language.unitSeparator + this.writtenNumber(m))
      }
    }

    // Process number item
    res = this.processNumber(res, n, m)

    let firstSignificantN = 0
    if (this._firstSignificant !== undefined) {
      firstSignificantN = this._firstSignificant * Math.floor(n / this._firstSignificant)
    }

    const rest = n - firstSignificantN
    if (this._language.andWhenTrailing && this._firstSignificant && rest > 0 &&
      res[0].indexOf(this._language.unitSeparator) !== 0) {
      res = [res[0], this._language.unitSeparator.replace(/\s+$/, '')].concat(res.slice(1))
    }

    // Check Separator
    res = this.checkSeparator(res)

    const result = res.reverse().join(' ')
    return result
  }

  private handleSmallerThan100 (n: number) {
    const dec = Math.floor(n / 10) * 10
    const unit = n - dec

    if (unit) {
      return (this._language.base[dec] + this._language.baseSeparator + this.writtenNumber(unit))
    }
    return this._language.base[dec]
  }

  private selectLanguage (): void {
    switch (this._options.lang()) {
      case 'es':
        this._language = this._i18n.es
        break
      case 'gl':
        this._language = this._i18n.gl
        break
      default:
        this._language = this._i18n.gl
        break
    }
  }

  private processNumber (res: string[], n: number, m: number): string[] {
    let unit
    for (let i = 0, len = this._language.units.length; i < len; i++) {
      let r = Math.floor(n / this._scale[i])
      let divideBy

      if (i === len - 1) divideBy = 1000000
      else divideBy = this._scale[i + 1] / this._scale[i]

      r %= divideBy
      unit = this._language.units[i]

      if (!r) {
        continue
      }
      this._firstSignificant = this._scale[i]

      if (unit.useBaseInstead) {
        const shouldUseBaseException = unit.useBaseException.indexOf(r) > -1 &&
            (unit.useBaseExceptionWhenNoTrailingNumbers ? i === 0 && res.length : true)

        if (!shouldUseBaseException) {
          res.push(this._language.base[r * this._scale[i]])
        } else {
          res.push(r > 1 && unit.plural ? unit.plural : unit.singular)
        }
        continue
      }

      let str = unit.plural && (!unit.avoidInNumberPlural || !m)
        ? unit.plural : unit.singular

      str = (r === 2 && unit.dual) ? unit.dual : str
      str = (r > 10 && unit.restrictedPlural) ? unit.singular : str

      if (
        unit.avoidPrefixException &&
        unit.avoidPrefixException.indexOf(r) > -1
      ) {
        res.push(str)
        continue
      }

      const exception = this._language.unitExceptions[r]
      const number = exception || this.writtenNumber(r)
      n -= r * this._scale[i]
      res.push(number + ' ' + str)
    }

    return res
  }

  private checkSeparator (res: string[]): string[] {
    if (this._language.allSeparator) {
      for (let j = 0; j < res.length - 1; j++) {
        res[j] = this._language.allSeparator + res[j]
      }
    }
    return res
  }
}
